import React, { useMemo, useState } from 'react';
import { PACKAGE_FEATURE_DRAG_TYPE } from './collectPackageFeatures';
import './package-feature-library.css';

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const DragIndicatorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="5" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="9" cy="19" r="1.5" />
    <circle cx="15" cy="5" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="15" cy="19" r="1.5" />
  </svg>
);

const PackageFeatureLibraryPanel = ({
  open,
  onClose,
  libraryGroups,
  onSelectFeature,
  onAddCustomFeature,
}) => {
  const [query, setQuery] = useState('');

  const filteredGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return libraryGroups;

    return libraryGroups
      .map((group) => ({
        ...group,
        features: group.features.filter((feature) =>
          feature.text.toLowerCase().includes(normalizedQuery)
          || group.packageName.toLowerCase().includes(normalizedQuery)
        ),
      }))
      .filter((group) => group.features.length > 0);
  }, [libraryGroups, query]);

  const totalFeatures = filteredGroups.reduce((sum, group) => sum + group.features.length, 0);

  const handleDragStart = (event, featureText) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData(PACKAGE_FEATURE_DRAG_TYPE, featureText);
    event.dataTransfer.setData('text/plain', featureText);
  };

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="pkg-feature-library__backdrop"
        aria-label="Close feature library"
        onClick={onClose}
      />
      <aside className="pkg-feature-library" aria-label="Feature library">
        <div className="pkg-feature-library__header">
          <div>
            <h3 className="pkg-feature-library__title">Feature library</h3>
            <p className="pkg-feature-library__subtitle">
              Drag or click features from your other packages
            </p>
          </div>
          <button type="button" className="pkg-btn-icon" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="pkg-feature-library__search">
          <input
            type="search"
            className="pkg-input"
            placeholder="Search features or packages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="pkg-feature-library__body">
          {totalFeatures === 0 ? (
            <p className="pkg-feature-library__empty">
              {libraryGroups.length === 0
                ? 'No features found on other packages yet.'
                : 'No features match your search.'}
            </p>
          ) : (
            filteredGroups.map((group) => (
              <section key={group.packageId} className="pkg-feature-library__group">
                <h4 className="pkg-feature-library__group-title">{group.packageName}</h4>
                <ul className="pkg-feature-library__list">
                  {group.features.map((feature) => (
                    <li key={`${group.packageId}-${feature.text}`}>
                      <button
                        type="button"
                        className={`pkg-feature-library__item ${
                          feature.alreadyAdded ? 'pkg-feature-library__item--added' : ''
                        }`}
                        draggable={!feature.alreadyAdded}
                        disabled={feature.alreadyAdded}
                        onDragStart={(event) => handleDragStart(event, feature.text)}
                        onClick={() => onSelectFeature(feature.text)}
                        title={
                          feature.alreadyAdded
                            ? 'Already on this package'
                            : 'Click to add or drag into the list'
                        }
                      >
                        <span className="pkg-feature-library__drag" aria-hidden="true">
                          <DragIndicatorIcon />
                        </span>
                        <span className="pkg-feature-library__text">{feature.text}</span>
                        {feature.alreadyAdded && (
                          <span className="pkg-feature-library__badge">Added</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        <div className="pkg-feature-library__footer">
          <button
            type="button"
            className="pkg-btn pkg-btn-secondary pkg-feature-library__custom-btn"
            onClick={onAddCustomFeature}
          >
            Add custom feature
          </button>
        </div>
      </aside>
    </>
  );
};

export default PackageFeatureLibraryPanel;
